import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { CropImageStore } from 'shared/ui/CropImage'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { showToast, Typography } from 'shared/ui'
import { type UserOwner } from 'entities/Users/model/UserOwner'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { UsersApi } from 'entities/Users'
import { ProfileStore } from 'pages/settings/pages/personal/pages/account/pages/profile'
import { UploadPhotoModal } from 'pages/settings/pages/personal/pages/account/ui/UploadPhotoModal/UploadPhotoModal'
import { UploadPhotoModalActions } from 'pages/settings/pages/personal/pages/account/ui/UploadPhotoModal/UploadPhotoModalActions'

export class AccountStore {
  private _loading = false
  private _modalId = nanoid()
  private readonly _cropImageStore: CropImageStore
  private readonly _profileStore: ProfileStore

  constructor(private _user: UserOwner, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._profileStore = new ProfileStore(this._user, this._entities)
    this._cropImageStore = new CropImageStore()
    this.init()
  }

  init = () => {
    this._profileStore.init()
  }

  handleUploadPhoto = (file: File) => {
    if (file instanceof File) this._cropImageStore.setFile(file)

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Adjust your image',
      ModalContent: () => <UploadPhotoModal store={this} />,
      ModalActions: () => <UploadPhotoModalActions store={this} />,
      showCloseButton: false,
      width: 360,
      onClose: () => this.onCloseModal(),
    })
  }

  onCloseModal = () => {
    modalStore.removeModal(this._modalId)
    this._cropImageStore.reset()
  }

  handleApplyUploadImage = async () => {
    try {
      if (this._loading) return

      runInAction(() => {
        this._loading = true
      })

      const base64 = await this._cropImageStore.getCroppedBase64Png()

      if (base64) {
        this._profileStore.updatePhotoUrl(base64)
        const saved = await this._profileStore.onSaveProfile()

        runInAction(() => {
          if (saved) {
            this._user.photo_url = base64

            showToast({
              type: 'success',
              title: 'Image applied',
            })
          }
        })
      }

      this.onCloseModal()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  openChangeRoleRequestModal = () => {
    modalStore.addModal({
      id: this._modalId,
      title: 'Request role upgrade',
      desc: (
        <Typography
          variant={'body-lg-regular'}
          ariaLabel={'Request role description'}
          color={'var(--content-primary-primary)'}
        >
          A request will be sent to your organization owner. Once approved, your access will be
          upgraded.
        </Typography>
      ),
      type: ModalTypeList.INFO,
      showCloseButton: false,
      primaryAction: {
        text: 'Send request',
        onAction: async () => {
          await this.updateChangeRoleRequest()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(this._modalId)
        },
      },
    })
  }

  updateChangeRoleRequest = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      await UsersApi.updateChangeRoleRequest()

      showToast({
        type: 'success',
        title: 'Role upgrade request sent',
      })
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    } finally {
      runInAction(() => {
        modalStore.closeModal(this._modalId)
        this._loading = false
      })
    }
  }

  reset = () => {
    this.profileStore.reset()
    this.cropImageStore.reset()
  }

  get loading() {
    return this._loading
  }

  get user() {
    return this._user
  }

  get profileStore() {
    return this._profileStore
  }

  get cropImageStore() {
    return this._cropImageStore
  }
}
